import { observable, computed, decorate, flow, action } from 'mobx'
import axios from 'axios'
import axiosConfig from 'src/axios.config'

/**
 * Describes the store containing al info about the authenticated user.
 */
class AuthenticatedUserStore {
    static IMPERSONATE = {
        USER: 'users',
        ORGANIZATION: 'organization',
    }

    static APP_PERMISSIONS = 'app_permissions'

    constructor() {
        /**
         * @Observable
         * @type {object}
         */
        this.userData = {}
        this.authToken = ''
        this.responses = {}
        this.loading = { app_permissions: false }
        this.loadingPromises = {}
        this.impersonated = {}
        this.selectedOrganization = null

        // TODO: refactor this store to follow the more common loadX pattern with computed props for reading/parsing data
        this._permissions = []
    }

    /**
     * @computed
     * @returns {string}
     */
    get email() {
        return this.userData.email
    }

    /**
     * @computed
     * @returns {string}
     */
    get firstName() {
        return this.userData.firstName
    }

    /**
     * @computed
     * @returns {string}
     */
    get lastName() {
        return this.userData.lastName
    }

    /**
     * @computed
     * @returns {string}
     */
    get username() {
        return this.userData.username
    }

    /**
     * @computed
     * @returns {boolean}
     */
    get isGlobalAdmin() {
        return this.userData.is_global_admin
    }

    /**
     * @computed
     * @returns {boolean}
     */
    get isLocalUser() {
        return this.userData.is_local_user
    }

    /**
     * @computed
     * @returns {string}
     */
    get impersonatedOrganization() {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        return this.impersonated[ORGANIZATION]?.label
    }

    /**
     * @computed
     * @returns {string}
     */
    get impersonatedOrganizationId() {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        return this.impersonated[ORGANIZATION]?.id
    }

    /**
     * @type {Function & IAction}
     */
    setUserData = action(data => {
        this.userData = data
        if (!this.userData.is_local_user) {
            this.loadUserOrganizations()
            if (this.userData.is_global_admin) {
                this.loadUsers()
            }
        }
    })

    /**
     * @type {Function & IAction}
     */
    setAuthToken = action(token => {
        this.authToken = token
        axiosConfig.headers.common.Authorization = token
    })

    /**
     * @type {Function & IAction}
     */
    setPermissions = action(permissions => {
        this._permissions = permissions
    })

    setSelectedOrganization = action(organization => {
        this.selectedOrganization = organization
    })

    setImpersonatedOrganization = action(organization => {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        this.impersonated[ORGANIZATION] = organization
    })

    loadUserOrganizations = flow(function* () {
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        this.loading[ORGANIZATION] = true
        try {
            this.loadingPromises[ORGANIZATION] = axios.get(
                `/api/auth/users/${this.userData.username}/tenants`
            )
            this.responses[ORGANIZATION] =
                yield this.loadingPromises[ORGANIZATION]
        } catch (ex) {
            return Promise.reject(ex)
        } finally {
            this.loading[ORGANIZATION] = false
        }
    })

    loadUsers = flow(function* () {
        const { USER } = AuthenticatedUserStore.IMPERSONATE
        this.loading[USER] = true
        try {
            this.loadingPromises[USER] = axios.get(`/api/auth/users`, {
                params: {
                    per_page: 1000,
                    sort: '+username',
                },
            })
            this.responses[USER] = yield this.loadingPromises[USER]
        } catch (ex) {
            return Promise.reject(ex)
        } finally {
            this.loading[USER] = false
        }
    })

    loadPermissions = flow(function* (user, organization) {
        try {
            let params = { app_key: 'auth_ui' }
            if (organization) {
                params.organization_id = organization
            }
            const response = yield axios.get(
                `/api/auth/users/${user}/app-permissions`,
                {
                    params,
                }
            )
            const { app_permissions } = response.data
            if (!app_permissions || app_permissions.length === 0) {
                this.setPermissions([])
            } else {
                this.setPermissions(app_permissions[0].actions)
            }
        } catch (e) {
            return Promise.reject(e)
        }
    })

    getAppPermissions = flow(function* (user, organization) {
        if (this.loading[AuthenticatedUserStore.APP_PERMISSIONS]) {
            return
        }
        this.loading[AuthenticatedUserStore.APP_PERMISSIONS] = true
        let params = { app_key: 'auth_ui' }
        if (organization) {
            params.organization_id = organization
        }
        try {
            this.loadingPromises[AuthenticatedUserStore.APP_PERMISSIONS] =
                axios.get(`/api/auth/users/${user}/app-permissions`, {
                    params,
                })
            this.responses[AuthenticatedUserStore.APP_PERMISSIONS] =
                yield this.loadingPromises[
                    AuthenticatedUserStore.APP_PERMISSIONS
                ]
        } catch (ex) {
            return Promise.reject(ex)
        } finally {
            this.loading[AuthenticatedUserStore.APP_PERMISSIONS] = false
        }
    })

    impersonateOrganization = flow(function* (organization) {
        try {
            const response = yield axios.post('/api/auth/impersonator', {
                organization_id: organization.value,
            })
            this.setAuthToken(response.headers.authorization)
            this.setImpersonatedOrganization(organization)
            this.setSelectedOrganization(organization)
            this.loadPermissions(this.userData.username, organization.value)
        } catch (e) {
            console.error(e)
            return Promise.reject(e)
        }
    })

    stopImpersonateOrganization = flow(function* () {
        try {
            const response = yield axios.delete('/api/auth/impersonator', {
                params: {
                    organization_only: true,
                },
            })
            this.setAuthToken(response.headers.authorization)
            this.setImpersonatedOrganization(null)
            this.setSelectedOrganization(null)
            this.loadPermissions(this.userData.username, null)
        } catch (e) {
            console.error(e)
            return Promise.reject(e)
        }
    })

    get canGlobalAdmin() {
        let parsedResponse = []
        if (
            this.responses[AuthenticatedUserStore.APP_PERMISSIONS] &&
            this.responses[AuthenticatedUserStore.APP_PERMISSIONS].data
        ) {
            parsedResponse =
                this.responses[AuthenticatedUserStore.APP_PERMISSIONS].data
                    .app_permissions[0].actions
        } else {
            // to avoid unauthorized page flashing briefly once the user refreshes the page
            return null
        }
        return parsedResponse.includes('auth:global-admin:request')
    }

    get userOrganizations() {
        let parsedResponse = []
        const { ORGANIZATION } = AuthenticatedUserStore.IMPERSONATE
        if (this.responses[ORGANIZATION] && this.responses[ORGANIZATION].data) {
            parsedResponse = this.responses[ORGANIZATION].data.tenants
        }
        return parsedResponse
    }

    get userOrganization() {
        if (this.selectedOrganization) {
            return this.selectedOrganization.label
        }
        const tenants = this.userOrganizations
        for (let i = 0; i < tenants.length; i++) {
            if (tenants[i].organization_id === this.userData.organization_id) {
                return tenants[i].name
            }
        }
        return ''
    }

    get users() {
        let parsedResponse = []
        const { USER } = AuthenticatedUserStore.IMPERSONATE
        if (this.responses[USER] && this.responses[USER].data) {
            parsedResponse = this.responses[USER].data.results.filter(
                user => !user.is_local_user
            )
        }
        return parsedResponse
    }

    /**
     * This parses permissions like documents:document:create
     * to { documents: { document: { create: true } } }
     */
    get permissions() {
        let permissions = {}
        this._permissions.forEach(permission => {
            let splitPermission = permission.split(':')
            let temp = permissions

            while (splitPermission.length) {
                let current = splitPermission.shift()
                if (!temp[current]) {
                    temp[current] = splitPermission.length ? {} : true
                }
                temp = temp[current]
            }
        })
        return permissions
    }

    get currentOrganizationId() {
        if (this.selectedOrganization) {
            return this.selectedOrganization.id
        } else if (this.impersonatedOrganizationId) {
            return this.impersonatedOrganizationId
        } else {
            const tenants = this.userOrganizations
            for (let i = 0; i < tenants.length; i++) {
                if (
                    tenants[i].organization_id === this.userData.organization_id
                ) {
                    return tenants[i].id
                }
            }
            return null
        }
    }
}

export default decorate(AuthenticatedUserStore, {
    userData: observable,
    authToken: observable,
    _permissions: observable,
    loading: observable,
    responses: observable,
    selectedOrganization: observable,
    impersonatedOrganization: computed,
    impersonatedOrganizationId: computed,
    currentOrganizationId: computed,
    email: computed,
    firstName: computed,
    lastName: computed,
    username: computed,
    isGlobalAdmin: computed,
    isLocalUser: computed,
    userOrganizations: computed,
    userOrganization: computed,
    users: computed,
    permissions: computed,
    canGlobalAdmin: computed,
})
